import { createEffect, createEvent, createStore, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { AddonCode, BillingAddonCode, PricesDto } from '@nx-frontend/shared/types';
import {
  BillingInfoResponse,
  BillingPlanResponse,
  PaywallMode,
  PaywallPlan,
  BillingPlan,
  BillingUsageInfoResponse,
  PaywallBillingPeriod,
  BillingBillingInfoResponse,
  SubscriptionResponse,
  BillingPlanCode,
  BillingAddon,
  PaywallAddonCardItem,
  CouponsResponse,
  CouponRedemption,
} from '@nx-frontend/shared/types/paywall-types';
import { getPaywallActionDiscounts } from './action-discounts';

interface PaywallState {
  mode?: PaywallMode;
  plans: PaywallPlan[];
  addons: PaywallAddonCardItem[];
  prices: PricesDto | Record<never, string>;
  selectedPlan: BillingPlanCode | null;
  fetching: boolean | null;
  settings: PaywallStateSettings;
  onlyMembersCnt: number;
  billingPeriod: 'monthly' | 'yearly';
  fetchingOrderSummary: boolean | null;
  fetchingConfirmSubscription: boolean;
  subscriptionId: string;
  total: number;
  credits: number;
  paid: number;
  discount: number;
  billedToday: number;
  prorate: number;
  totalWithoutProrate: number;
  currentSubscriptionTotal: number;
  cardType: string;
  cardLastFour: string;
  currentPeriodEndsAt: string;
  couponError: string | null;
  couponApplied: boolean;
  currentPlanCode: BillingPlanCode | null;
  currentPlan: BillingPlan | null;
  selectedAddons: Record<BillingAddonCode, BillingAddon> | Record<never, BillingAddon>;
  currentAddons: BillingAddon[];
  orderSummaryAbortController?: AbortController | null;
  confirmError: boolean;
  billingPlans: BillingPlan[];
  orgBillingInfo: BillingInfoResponse | null;
  currentAddonsToBill: BillingAddon[];
  couponRedemption: CouponRedemption[];
  /**
   * Members in recurly if have subscription, else onlyMembersCnt
   */
  members: number;
  prorationRate: number;
  /**
   * Discount from forever coupons in percents
   */
  foreverCouponsDiscount: number;
  /**
   * Difference between old subscription and new one
   */
  prorateDiff: number;
  previewError: boolean;
}

export interface PaywallStateSettings {
  gatewayUrl: string;
  userBillingUrl: string;
  sessionId: string;
  orgId: string;
  userId: number;
  showCouponAppliedToast?: () => void;
  showSuccessToast?: (description: string) => void;
  showErrorToast?: (description: string) => void;
  closePaywall?: () => void;
  addonMode?: boolean;
}

const initialPaywallState: PaywallState = {
  mode: undefined,
  plans: [],
  addons: [],
  prices: {},
  selectedPlan: null,
  fetching: null,
  fetchingOrderSummary: null,
  fetchingConfirmSubscription: false,
  onlyMembersCnt: 0,
  billingPeriod: 'yearly',
  subscriptionId: '',
  total: 0,
  credits: 0,
  paid: 0,
  discount: 0,
  billedToday: 0,
  prorate: 0,
  totalWithoutProrate: 0,
  currentSubscriptionTotal: 0,
  cardType: '',
  cardLastFour: '',
  currentPeriodEndsAt: '',
  couponError: null,
  couponApplied: false,
  currentPlanCode: null,
  currentPlan: null,
  selectedAddons: {},
  currentAddons: [],
  orderSummaryAbortController: null,
  confirmError: false,
  billingPlans: [],
  orgBillingInfo: null,
  currentAddonsToBill: [],
  members: 0,
  foreverCouponsDiscount: 0,
  couponRedemption: [],
  settings: {
    gatewayUrl: '',
    userBillingUrl: '',
    sessionId: '',
    orgId: '',
    userId: 0,
    showCouponAppliedToast: () => console.log('Coupon applied'),
    showSuccessToast: () => console.log('Success toast'),
    showErrorToast: () => console.log('Error toast'),
  },
  prorationRate: 1,
  prorateDiff: 0,
  previewError: false,
};

export const $paywallStore = createStore(initialPaywallState);

/**
 * Init store settings
 */
export const initPaywallSettingsEvent = createEvent<{ mode?: PaywallMode; settings: PaywallStateSettings }>();

$paywallStore.on(initPaywallSettingsEvent, (_, payload) => {
  return {
    ...initialPaywallState,
    ...payload,
  };
});

/**
 * Fetch paywall event
 */
export const fetchPaywallEvent = createEvent<((store: PaywallState) => void) | void>();

const getPlanColumn = (
  code: BillingPlanCode,
  params: {
    billingPlans: BillingPlanResponse | null;
    billingPeriod: PaywallBillingPeriod;
    members: number;
    isFreePlan: boolean;
    mode?: PaywallMode;
  }
): {
  code: BillingPlanCode;
  title: string;
  price: number;
  planPrice: number;
  extraMemberPrice: number;
  exactPlanPrice: number;
  exactExtraMemberPrice: number;
  minMembers: number;
  defaultAddons: BillingAddon[];
} => {
  const { billingPlans, billingPeriod, members, mode } = params;
  if (!billingPlans) {
    return {
      code,
      title: '',
      price: 0,
      planPrice: 0,
      extraMemberPrice: 0,
      exactPlanPrice: 0,
      exactExtraMemberPrice: 0,
      minMembers: 0,
      defaultAddons: [],
    };
  }

  const plan: BillingPlan | undefined = billingPlans?.plans.find((plan) => plan.code === code);
  const title = plan?.name.en || '';
  const planPrice = plan?.planPrice || 0;
  const extraMemberPrice = plan?.addons.find((addon) => addon.code === 'members')?.price || 0;

  const divisor = billingPeriod === 'yearly' ? 12 : 1;

  const plusPlanPrice =
    (mode === 'trialEnd' || mode === 'upgrade') && plan?.minMembers !== undefined
      ? members - plan.minMembers > 0
        ? ((members - plan.minMembers) * extraMemberPrice) / divisor
        : 0
      : 0;

  return {
    code,
    title,
    price: Math.round((planPrice / divisor) * 1) / 1 + plusPlanPrice,
    planPrice: planPrice / divisor,
    extraMemberPrice: extraMemberPrice / divisor,
    exactPlanPrice: planPrice,
    exactExtraMemberPrice: extraMemberPrice,
    minMembers: plan?.minMembers || 0,
    defaultAddons: plan?.defaultAddons || [],
  };
};

export const fetchPaywallFx = createEffect(
  async (params: PaywallState & { callback?: (store: PaywallState) => void }) => {
    const { callback, ...store } = params;
    const { settings } = store;
    const headers = {
      'everhelper-session-id': settings.sessionId,
    };

    const promises = [
      axiosClient.get<BillingPlanResponse>(`${settings.gatewayUrl}/ft:billing/plans`, { headers }),
      axiosClient.get<PricesDto>(`${settings.gatewayUrl}/ft:billing/prices`, { headers }),
      axiosClient
        .get<BillingInfoResponse | null>(`${settings.gatewayUrl}/ft:org/orgs/${settings.orgId}/billingInfo`, {
          headers,
        })
        .catch(() => null),
      axiosClient.get<BillingUsageInfoResponse>(`${settings.gatewayUrl}/ft:org/orgs/${settings.orgId}/usageInfo`, {
        headers,
      }),
      axiosClient
        .get<BillingBillingInfoResponse | null>(
          `${settings.gatewayUrl}/ft:billing/orgs/${settings.orgId}/billingInfo`,
          {
            headers,
          }
        )
        .catch(() => null),
    ];

    const [plansResponse, pricesResponse, billingInfoResponse, billingUsageInfoResponse, billingBillingInfoResponse] =
      await Promise.all(promises);

    let billingPlans: BillingPlanResponse | null = null;
    let billingPrices: PricesDto | Record<never, string> = {};
    let orgBillingInfo: BillingInfoResponse | null = null;
    let billingUsageInfo: BillingUsageInfoResponse | null = null;
    let billingPeriod: PaywallBillingPeriod = 'yearly';
    let billingBillingInfo: BillingBillingInfoResponse | null = null;

    let subscriptionId = '';
    let currentPlanCode: BillingPlanCode | null = null;
    let currentPlan: BillingPlan | null = null;
    let subscription: SubscriptionResponse | null = null;

    let currentPeriodEndsAt = '';
    let currentSubscriptionTotal = 0;
    const currentAddons: BillingAddon[] = [];

    if (plansResponse?.status === 200) {
      billingPlans = plansResponse.data as BillingPlanResponse;
    }
    if (pricesResponse?.status === 200) {
      billingPrices = pricesResponse.data as PricesDto;
    }
    if (billingInfoResponse?.status === 200) {
      orgBillingInfo = billingInfoResponse.data as BillingInfoResponse;
      if (orgBillingInfo.plan.match(/monthly/) && orgBillingInfo.active) {
        billingPeriod = 'monthly';
      }
      subscriptionId = orgBillingInfo.subscriptionId;
      const { data } = await axiosClient.get<SubscriptionResponse>(
        `${settings.gatewayUrl}/ft:billing/subscriptions/${subscriptionId}`,
        { headers }
      );
      if (data && data.state === 'active') {
        subscription = data;
        currentPeriodEndsAt = subscription.currentPeriodEndsAt;
        currentPlanCode = subscription.plan.code as BillingPlanCode;
        currentPlan = billingPlans?.plans.find((plan) => plan.code === currentPlanCode) || null;
        subscription.addOns.forEach((addon) => {
          currentAddons.push({
            code: addon.addOn.code as BillingAddonCode,
            quantity: addon.quantity,
          });
        });
        currentSubscriptionTotal = subscription.total;
      }
    }
    if (billingUsageInfoResponse?.status === 200) {
      billingUsageInfo = billingUsageInfoResponse.data as BillingUsageInfoResponse;
    }
    if (billingBillingInfoResponse?.status === 200) {
      billingBillingInfo = billingBillingInfoResponse.data as BillingBillingInfoResponse;
    }

    const onlyMembersCnt = billingUsageInfo?.members.current || 1;

    let members = onlyMembersCnt > (currentPlan?.minMembers || 0) ? onlyMembersCnt : currentPlan?.minMembers || 0;
    // get members addon from recurly
    const currentMembersAddon = prepareAddons(currentAddons, { allowMembers: true }).find(
      (addon) => addon.code === 'members'
    ) || { code: 'members', quantity: 1 };

    members = (currentMembersAddon?.quantity || 1) > members ? currentMembersAddon?.quantity || 1 : members;

    const getPlanColumnParams = {
      billingPlans,
      billingPeriod,
      members,
      isFreePlan: !subscription,
      mode: store.mode,
    };

    // const newLimits = [
    //   {
    //     ...getPlanColumn(`plus2024-${billingPeriod}`, getPlanColumnParams),
    //     featuresLocalized: [
    //       //{ locKey: 'features.paywall.plans_features.client_accounts', props: { num: 2 } },
    //       { locKey: 'features.paywall.plans_features.team_members', props: { num: 1 } },
    //       { locKey: 'features.paywall.plans_features.client_portals', props: { num: 1 } },
    //       { locKey: 'features.paywall.plans_features.gen_ai_requests', props: { num: 1000 } },
    //       { locKey: 'features.paywall.plans_features.automation_runs', props: { num: 1000 } },
    //       { locKey: 'features.paywall.plans_features.transcribing_hours', props: { num: 5 } },
    //       { locKey: 'features.paywall.plans_features.trial_period', props: { num: '14 _days_' } },
    //     ],
    //   },
    //   {
    //     ...getPlanColumn(`team2024-${billingPeriod}`, getPlanColumnParams),
    //     isMostPopular: true,
    //     featuresLocalized: [
    //       //{ locKey: 'features.paywall.plans_features.client_accounts', props: { num: 15 } },
    //       { locKey: 'features.paywall.plans_features.team_members', props: { num: 5 } },
    //       { locKey: 'features.paywall.plans_features.client_portals', props: { num: '10' } },
    //       { locKey: 'features.paywall.plans_features.gen_ai_requests', props: { num: '10,000' } },
    //       { locKey: 'features.paywall.plans_features.automation_runs', props: { num: '10,000' } },
    //       { locKey: 'features.paywall.plans_features.transcribing_hours', props: { num: 1000 } },
    //       { locKey: 'features.paywall.plans_features.trial_period', props: { num: '14 _days_' } },
    //     ],
    //   },
    //   {
    //     ...getPlanColumn(`business2024-${billingPeriod}`, getPlanColumnParams),
    //     featuresLocalized: [
    //       //{ locKey: 'features.paywall.plans_features.client_accounts', props: { num: 10000 } },
    //       { locKey: 'features.paywall.plans_features.team_members', props: { num: 50 } },
    //       {
    //         locKey: ['features.paywall.plans_features.client_portals', '(', 'features.paywall.contact_us', ')'],
    //         props: { num: '50/_unlimited_ ' },
    //       },
    //       { locKey: 'features.paywall.plans_features.gen_ai_requests', props: { num: '50,000' } },
    //       { locKey: 'features.paywall.plans_features.automation_runs', props: { num: '50,000' } },
    //       { locKey: 'features.paywall.plans_features.transcribing_hours', props: { num: 1000 } },
    //       { locKey: 'features.paywall.plans_features.trial_period', props: { num: '14 _days_' } },
    //     ],
    //     whiteLabel: true,
    //   },
    // ];

    const oldLimits = [
      {
        ...getPlanColumn(`plus2024-${billingPeriod}`, getPlanColumnParams),
        featuresLocalized: [
          { locKey: 'features.paywall.plans_features.client_accounts', props: { num: 2 } },
          { locKey: 'features.paywall.plans_features.client_portals', props: { num: 2 } },
          { locKey: 'features.paywall.plans_features.team_members', props: { num: 3 } },
          { locKey: 'features.paywall.plans_features.gen_ai_requests', props: { num: 99 } },
          { locKey: 'features.paywall.plans_features.automation_runs', props: { num: 99 } },
          { locKey: 'features.paywall.plans_features.transcribing_hours', props: { num: 5 } },
          { locKey: 'features.paywall.plans_features.trial_period', props: { num: '14 days' } },
        ],
      },
      {
        ...getPlanColumn(`team2024-${billingPeriod}`, getPlanColumnParams),
        isMostPopular: true,
        featuresLocalized: [
          { locKey: 'features.paywall.plans_features.client_accounts', props: { num: 15 } },
          { locKey: 'features.paywall.plans_features.client_portals', props: { num: 5 } },
          { locKey: 'features.paywall.plans_features.team_members', props: { num: 3 } },
          { locKey: 'features.paywall.plans_features.gen_ai_requests', props: { num: 300 } },
          { locKey: 'features.paywall.plans_features.automation_runs', props: { num: 300 } },
          { locKey: 'features.paywall.plans_features.transcribing_hours', props: { num: 19 } },
          { locKey: 'features.paywall.plans_features.trial_period', props: { num: '14 days' } },
        ],
      },
      {
        ...getPlanColumn(`business2024-${billingPeriod}`, getPlanColumnParams),
        featuresLocalized: [
          { locKey: 'features.paywall.plans_features.client_accounts', props: { num: '10,000' } },
          { locKey: 'features.paywall.plans_features.client_portals', props: { num: 15 } },
          { locKey: 'features.paywall.plans_features.team_members', props: { num: 3 } },
          { locKey: 'features.paywall.plans_features.gen_ai_requests', props: { num: '10,000' } },
          { locKey: 'features.paywall.plans_features.automation_runs', props: { num: '10,000' } },
          { locKey: 'features.paywall.plans_features.transcribing_hours', props: { num: 99 } },
          { locKey: 'features.paywall.plans_features.trial_period', props: { num: '14 days' } },
        ],
        whiteLabel: true,
      },
    ];

    const plans: PaywallPlan[] = [
      ...oldLimits,
      {
        code: 'unlimited',
        title: 'Unlimited',
        price: 0,
        planPrice: 0,
        extraMemberPrice: 0,
        exactPlanPrice: 0,
        exactExtraMemberPrice: 0,
        minMembers: 0,
        defaultAddons: [],
        featuresLocalized: [
          { locKey: 'features.paywall.plans_features.client_accounts', props: { num: '_unlimited_' } }, // TODO: comment after set newLimits
          { locKey: 'features.paywall.plans_features.client_portals', props: { num: '_unlimited_' } },
          { locKey: 'features.paywall.plans_features.team_members', props: { num: '_unlimited_' } },
          { locKey: 'features.paywall.plans_features.gen_ai_requests', props: { num: '_unlimited_' } },
          { locKey: 'features.paywall.plans_features.automation_runs', props: { num: '_unlimited_' } },
          { locKey: 'features.paywall.plans_features.transcribing_hours', props: { num: '50,000' } },
          {
            locKey: ['features.paywall.plans_features.trial_period', 'features.paywall.contact_us'],
            props: { num: '' },
          },
        ],
        whiteLabel: true,
        isSelectable: false,
      },
    ];

    const aiAddonDisabled = !!currentAddons.find((addon) => addon.code === 'ai');
    const divisor = billingPeriod === 'yearly' ? 12 : 1;
    const selectedPlan = store.selectedPlan || currentPlanCode;
    const prices = billingPrices as PricesDto;

    const addons: PaywallAddonCardItem[] = [
      // {
      //   title: 'Assistant AI',
      //   price: prices[`ai_addon_${ selectedPlan } _en`] / divisor,
      //   perLabel: 'month',
      //   description: '1000 requests in add-on',
      //   showCounter: true,
      //   selectable: true,
      //   selected: false,
      // },
      {
        code: BillingAddonCode.PORTALS,
        title: 'Client Portal',
        titleLocKey: 'features.paywall.addons.client_portal.title',
        pricePerMonth: prices[`portals_addon_${selectedPlan}_en` as keyof PricesDto] / divisor,
        price: prices[`portals_addon_${selectedPlan}_en` as keyof PricesDto],
        perLabel: 'month',
        perLabelLocKey: 'features.paywall.addons.client_portal.per_label',
        description: '1 portal in add-on',
        descriptionLocKey: 'features.paywall.addons.client_portal.description',
        showCounter: true,
        selectable: true,
        value: 0,
      },
      {
        code: BillingAddonCode.AUTOMATION,
        title: 'Automation',
        titleLocKey: 'features.paywall.addons.automation.title',
        pricePerMonth: prices[`automation_addon_${selectedPlan}_en` as keyof PricesDto] / divisor,
        price: prices[`automation_addon_${selectedPlan}_en` as keyof PricesDto],
        perLabel: 'month',
        perLabelLocKey: 'features.paywall.addons.automation.per_label',
        description: '1000 runs in add-on',
        descriptionLocKey: 'features.paywall.addons.automation.description',
        showCounter: true,
        selectable: true,
        value: 0,
      },
      {
        code: BillingAddonCode.TRANSCRIBE,
        title: 'Transcribe',
        titleLocKey: 'features.paywall.addons.transcribe.title',
        pricePerMonth: prices[`transcribe_addon_${selectedPlan}_en` as keyof PricesDto] / divisor,
        price: prices[`transcribe_addon_${selectedPlan}_en` as keyof PricesDto],
        perLabel: 'month',
        perLabelLocKey: 'features.paywall.addons.transcribe.per_label',
        description: '20 hours in add-on',
        descriptionLocKey: 'features.paywall.addons.transcribe.description',
        showCounter: true,
        selectable: true,
        value: 0,
      },
      {
        code: BillingAddonCode.AI,
        title: 'Generative AI',
        titleLocKey: 'features.paywall.addons.generative_ai.title',
        pricePerMonth: prices[`ai_addon_${selectedPlan}_en` as keyof PricesDto] / divisor,
        price: prices[`ai_addon_${selectedPlan}_en` as keyof PricesDto],
        perLabel: 'month per member',
        perLabelLocKey: 'features.paywall.addons.generative_ai.per_label',
        description: 'unlimited requests for each member',
        descriptionLocKey: 'features.paywall.addons.generative_ai.description',
        label: !aiAddonDisabled ? 'You will be charged for all added members' : undefined,
        labelLocKey: !aiAddonDisabled ? 'features.paywall.addons.generative_ai.label' : undefined,
        showAddButton: true,
        selectable: true,
        disabled: aiAddonDisabled,
        value: 0,
      },
    ];

    const cardType = billingBillingInfo?.paymentMethod.cardType || '';
    const cardLastFour = billingBillingInfo?.paymentMethod.lastFour || '';

    return {
      plans,
      addons,
      prices,
      billingPeriod,
      onlyMembersCnt,
      subscriptionId,
      cardType,
      cardLastFour,
      currentPeriodEndsAt,
      currentPlanCode,
      currentPlan,
      currentAddons,
      currentSubscriptionTotal,
      orgBillingInfo,
      billingPlans: billingPlans?.plans || [],
      members,
    };
  }
);

const startFeatchPaywallEvent = createEvent();

sample({
  source: { paywallStore: $paywallStore },
  clock: fetchPaywallEvent,
  fn: ({ paywallStore }, callback) => {
    return { ...paywallStore, callback: callback ?? undefined };
  },
  filter: ({ paywallStore }) => {
    // Skip fetching if already fetching or gatewayUrl is not set
    return !!paywallStore.settings.gatewayUrl && !paywallStore.fetching;
  },
  target: [startFeatchPaywallEvent, fetchPaywallFx],
});

$paywallStore.on(startFeatchPaywallEvent, (state) => {
  return {
    ...state,
    fetching: true,
  };
});

$paywallStore.on(fetchPaywallFx.doneData, (state, fields) => {
  return {
    ...state,
    ...fields,
    fetching: false,
  };
});

sample({
  source: $paywallStore,
  clock: fetchPaywallFx.done,
  fn: (store, { params }) => {
    const { callback } = params;
    if (callback) {
      callback(store);
    }
  },
});

/**
 * Select plan event
 */
export const selectPlanEvent = createEvent<BillingPlanCode | null>();

$paywallStore.on(selectPlanEvent, (state, selectedPlan) => {
  return {
    ...state,
    total: 0,
    //credits: 0,
    paid: 0,
    balance: 0,
    billedToday: 0,
    couponError: null,
    couponApplied: false,
    selectedPlan,
  };
});

export const startFetchOrderSummaryEvent = createEvent();

$paywallStore.on(startFetchOrderSummaryEvent, (state) => {
  return {
    ...state,
    fetchingOrderSummary: true,
  };
});

/**
 * Function merge addons with the same code
 * @param addons
 * @param options
 * @returns
 */
const prepareAddons = (addons: BillingAddon[], options: { allowMembers?: boolean } = {}) => {
  const currentAddons: Record<AddonCode, BillingAddon> = {} as Record<AddonCode, BillingAddon>;
  addons.forEach((item) => {
    const code = item.code.replace(/[0-9]{1,}/g, '') as AddonCode;
    if (code === 'members' && !options.allowMembers) return true;

    if (currentAddons[code]) {
      currentAddons[code] = {
        code,
        quantity: (currentAddons[code].quantity || 0) + (item.quantity || 0),
      };
    } else {
      currentAddons[code] = item;
    }
  });
  return Object.values(currentAddons);
};

/**
 * Fix addons quantity by default plan addons
 * @param addons
 * @param defaultAddons
 * @returns
 */
const mergeAddonsWithDefult = (addons: BillingAddon[], defaultAddons: BillingAddon[]) => {
  return addons.map((addon) => {
    const planDefaultAddon = defaultAddons.find((da) => da.code === addon.code);
    let quantity = addon.quantity || 0;
    if (planDefaultAddon) {
      const defaultAddonQuantity = planDefaultAddon.quantity || 0;
      if (planDefaultAddon && quantity < defaultAddonQuantity) {
        quantity = defaultAddonQuantity;
      }
    }
    return {
      code: addon.code,
      quantity,
    };
  });
};

/**
 * In current subscription may be addons that are not included in the new plan.
 * @param currentAddons
 * @param defaultAddons
 * @param planCode
 * @param prices
 * @returns
 */
const getCurrentAddonsToBill = (
  currentAddons: BillingAddon[],
  defaultAddons: BillingAddon[],
  planCode: string,
  prices: PricesDto
): BillingAddon[] => {
  const currentAddonsWithPrice = currentAddons.map((a) => {
    const key = `${a.code}_addon_${planCode}_en` as keyof PricesDto;
    const price = (prices as PricesDto)[key];
    return {
      ...a,
      price,
    };
  });

  const currentAddonsToBill: BillingAddon[] = [];
  currentAddonsWithPrice.forEach((addon) => {
    const paidQuantity = (addon?.quantity || 0) - (defaultAddons.find((da) => da.code === addon.code)?.quantity || 0);
    if (paidQuantity > 0) {
      currentAddonsToBill.push({
        ...addon,
        quantity: paidQuantity,
      });
    }
  });

  return currentAddonsToBill;
};

const fetchOrderSummaryFx = createEffect(
  async (params: PaywallState & { callback?: (store: PaywallState) => void }) => {
    const { callback, ...store } = params;
    const {
      selectedPlan,
      subscriptionId,
      settings,
      selectedAddons,
      billingPlans,
      currentPlanCode,
      prices,
      members,
      addons,
    } = store;

    if (!subscriptionId || subscriptionId.match(/^trial/)) {
      return {};
    }

    const headers = {
      'everhelper-session-id': settings.sessionId,
    };

    const planCode = selectedPlan || currentPlanCode;
    const plan = billingPlans.find((plan) => plan.code === planCode);

    const currentAddons = mergeAddonsWithDefult(prepareAddons(store.currentAddons), plan?.defaultAddons || []);
    const otherAddons = prepareAddons(Object.values(selectedAddons).concat(currentAddons));
    const currentAddonsToBill = getCurrentAddonsToBill(
      store.currentAddons,
      plan?.defaultAddons || [],
      planCode as keyof PricesDto,
      prices as PricesDto
    );

    let membersToUpdate = members;
    if (plan && plan.minMembers > members) {
      membersToUpdate = plan.minMembers;
    }

    let previewError = false;

    const promises = [
      // Fetch subscription preview
      axiosClient
        .post(
          `${settings.gatewayUrl}/ft:billing/subscriptions/${subscriptionId}/preview`,
          {
            orgId: settings.orgId,
            plan: planCode,
            members: membersToUpdate,
            otherAddons,
          },
          { headers, validateStatus: (status) => status >= 200 && status < 500 }
        )
        .catch(() => {
          return null;
        }),
      // Fetch account active coupons
      axiosClient.get<CouponsResponse>(`${settings.gatewayUrl}/ft:billing/orgs/${settings.orgId}/coupons`, { headers }),
    ];

    const [previewResponse, couponsResponse] = await Promise.all(promises);

    let data = null;
    if (previewResponse?.status === 200) {
      data = previewResponse?.data;
    } else {
      previewError = true;
      settings.showErrorToast && settings.showErrorToast(previewResponse?.data.message);
    }
    const couponRedemption: CouponRedemption[] = couponsResponse?.data?.coupons || [];

    // calculate forever coupons discount
    const foreverCoupons = couponRedemption.filter((item) => {
      const { coupon } = item;
      if (coupon.duration === 'forever') {
        if (!coupon.appliesToAllPlans) {
          if (!coupon.plans?.some((p) => p.code === planCode)) {
            return false;
          }
        }
        return true;
      }

      return false;
    });
    const foreverCouponsDiscount = foreverCoupons.reduce((acc, item) => acc + item.coupon.discount.percent, 0);

    const paid = data?.invoiceCollection?.chargeInvoice?.paid || 0; // prorationRate * (priceToPay)
    const balance = data?.invoiceCollection?.chargeInvoice?.balance || 0; // total - paid
    //const total = data?.invoiceCollection?.chargeInvoice?.total || data?.unitAmount; // total - paid
    const creditPayments = data?.invoiceCollection?.chargeInvoice?.creditPayments;
    const discount = data?.invoiceCollection?.chargeInvoice?.discount || 0;
    const prorationRate = data?.invoiceCollection?.chargeInvoice?.lineItems?.[0]?.prorationRate || 1;

    const totalWithoutProrate = store.currentSubscriptionTotal; ///parseFloat(((total + discount) / prorationRate).toFixed(2));
    const prorate = parseFloat((totalWithoutProrate * (1 - prorationRate)).toFixed(2));

    // credit always include used amount from the current plan,
    // to calculate credits on user account need to subtract paid for the current plan
    const paidForCurrentPlan = parseFloat((store.currentSubscriptionTotal * prorationRate).toFixed(2));
    let creditPaymentsAmount =
      (creditPayments?.reduce((acc: number, item: { amount: number }) => acc + item.amount, 0) || 0) -
      paidForCurrentPlan;

    // paidForCurrentPlan - this is the amount that was paid for the current plan
    // it may be different from recurly +/- 0.1
    // that's why need to check if creditPaymentsAmount < 0.3
    if (creditPaymentsAmount < 0.3) {
      creditPaymentsAmount = 0;
    }

    const credits = parseFloat(creditPaymentsAmount.toFixed(2));

    let billedToday = balance;
    if (balance <= 0) {
      billedToday = 0;
    }

    // Calculate prorateDiff for order summary block
    let subTotal = 0;
    let prorateDiff = 0;
    if (data) {
      const showPlanItem = selectedPlan !== null && selectedPlan !== currentPlanCode;
      const paywallPlan = store.plans.find((p) => p.code === selectedPlan);
      if (showPlanItem) {
        const extraMembers = membersToUpdate - (paywallPlan?.minMembers || 0);
        subTotal += paywallPlan?.exactPlanPrice || 0;
        subTotal += (paywallPlan?.exactExtraMemberPrice || 0) * extraMembers;
      }
      Object.values(selectedAddons).forEach((item) => {
        const addon = addons.find((a) => a.code === item.code);
        const price = addon?.price || 0;
        subTotal += price * (item.quantity || 0);
      });
      prorateDiff = +(subTotal - credits - discount - billedToday).toFixed(2);
    }

    return {
      billedToday,
      balance,
      credits,
      paid,
      discount,
      prorationRate,
      prorate,
      totalWithoutProrate,
      currentAddonsToBill,
      foreverCouponsDiscount,
      fetchingOrderSummary: false,
      prorateDiff,
      previewError,
      couponRedemption,
    };
  }
);

let FETCH_ORDER_SUMMERY_TID: NodeJS.Timeout | number | null = null;
const fetchOrderSummaryDebouncedFx = createEffect(
  async (params: PaywallState & { callback?: (store: PaywallState) => void }) => {
    FETCH_ORDER_SUMMERY_TID && clearTimeout(FETCH_ORDER_SUMMERY_TID);
    return new Promise<Partial<PaywallState>>((resolve) => {
      FETCH_ORDER_SUMMERY_TID = setTimeout(async () => {
        const res = await fetchOrderSummaryFx(params);
        resolve(res);
      }, 500);
    });
  }
);

sample({
  source: { paywallStore: $paywallStore },
  clock: selectPlanEvent,
  fn: ({ paywallStore }) => {
    return paywallStore;
  },
  filter: ({ paywallStore }) => {
    // Skip fetching if already fetching or gatewayUrl is not set
    return (
      !!paywallStore.settings.gatewayUrl &&
      (!!paywallStore.selectedPlan || !!Object.keys(paywallStore.selectedAddons).length)
    );
  },
  target: [startFetchOrderSummaryEvent, fetchOrderSummaryDebouncedFx],
});

export const fetchOrderSummaryEvent = createEvent<((store: PaywallState) => void) | void>();

sample({
  source: { paywallStore: $paywallStore },
  clock: fetchOrderSummaryEvent,
  fn: ({ paywallStore }, callback) => {
    return { ...paywallStore, callback: callback ?? undefined };
  },
  filter: ({ paywallStore }) => {
    // Skip fetching if already fetching or gatewayUrl is not set
    return !!paywallStore.settings.gatewayUrl;
  },
  target: [startFetchOrderSummaryEvent, fetchOrderSummaryDebouncedFx],
});

$paywallStore.on(fetchOrderSummaryDebouncedFx.doneData, (state, fields) => {
  return {
    ...state,
    ...fields,
    fetchingOrderSummary: false,
  };
});

sample({
  source: $paywallStore,
  clock: fetchOrderSummaryDebouncedFx.done,
  fn: (store, { params }) => {
    const { callback } = params;
    if (callback) {
      callback(store);
    }
  },
});

/**
 * Applay coupon event
 */
export const applyCouponEvent = createEvent<string>();

const applyCouponFx = createEffect(async (payload: { coupon: string; paywallStore: PaywallState }) => {
  const { coupon, paywallStore } = payload;

  const headers = {
    'everhelper-session-id': paywallStore.settings.sessionId,
  };

  let couponError: string | null = null;
  let couponApplied = false;
  try {
    const couponResponse = await axiosClient.post(
      `${paywallStore.settings.gatewayUrl}/ft:billing/coupons/${coupon}/apply`,
      {
        plan: paywallStore.selectedPlan || paywallStore.currentPlanCode,
      },
      {
        headers,
        validateStatus: (status) => {
          return status >= 200 && status < 500;
        },
      }
    );
    const data = couponResponse.data;
    if (couponResponse.status > 200) {
      couponError = data.message;
    } else {
      couponApplied = true;
      const action = getPaywallActionDiscounts();
      if (action?.coupon !== coupon) {
        const { showCouponAppliedToast } = paywallStore.settings;
        showCouponAppliedToast && showCouponAppliedToast();
      }
    }
  } catch (error) {
    const err = error as Error;
    couponError = err.message;
  }

  return {
    couponError,
    couponApplied,
  };
});

sample({
  source: { paywallStore: $paywallStore },
  clock: applyCouponEvent,
  fn: ({ paywallStore }, coupon) => {
    return { paywallStore, coupon };
  },
  target: [startFetchOrderSummaryEvent, applyCouponFx],
});

// Fetch order summary after coupon applied
sample({
  source: { paywallStore: $paywallStore },
  clock: applyCouponFx.doneData,
  fn: ({ paywallStore }) => {
    return paywallStore;
  },
  filter: ({ paywallStore }) => {
    return paywallStore.couponApplied;
  },
  target: [fetchOrderSummaryEvent],
});

$paywallStore.on(applyCouponFx.doneData, (state, fields) => {
  return {
    ...state,
    ...fields,
    fetchingOrderSummary: false,
  };
});

/**
 * Change addon count event
 */
interface ChangeAddonCountEventPayload {
  code: BillingAddonCode;
  quantity: number;
}
export const changeAddonCountEvent = createEvent<ChangeAddonCountEventPayload>();

$paywallStore.on(changeAddonCountEvent, (state, { code, quantity }) => {
  const selectedAddons = { ...state.selectedAddons } as Record<BillingAddonCode, BillingAddon>;

  if (selectedAddons[code]) {
    selectedAddons[code] = { ...selectedAddons[code], quantity };
  } else {
    selectedAddons[code] = { code, quantity };
  }

  if (quantity === 0) {
    delete selectedAddons[code];
  }

  return {
    ...state,
    selectedAddons,
  };
});

/**
 * Confirm subscription event
 */
export const confirmSubscriptionEvent = createEvent();

const startConfirmSubscriptionEvent = createEvent();

$paywallStore.on(startConfirmSubscriptionEvent, (state) => {
  return {
    ...state,
    fetchingOrderSummary: true,
    fetchingConfirmSubscription: true,
  };
});

const confirmSubscriptionFx = createEffect(async (store: PaywallState) => {
  const { selectedPlan, settings, selectedAddons, billingPlans, currentPlanCode, members, mode } = store;

  const headers = {
    'everhelper-session-id': settings.sessionId,
  };

  const planCode = selectedPlan || currentPlanCode;
  const plan = billingPlans.find((plan) => plan.code === planCode);
  const currentAddons = mergeAddonsWithDefult(prepareAddons(store.currentAddons), plan?.defaultAddons || []);
  const otherAddons = prepareAddons(Object.values(selectedAddons).concat(currentAddons));

  let membersToUpdate = members;
  if (plan && plan.minMembers > members) {
    membersToUpdate = plan.minMembers;
  }

  let confirmError = false;
  try {
    await axiosClient.post(
      `${settings.gatewayUrl}/ft:billing/orgs/${settings.orgId}/upgradeSubscription`,
      {
        orgId: settings.orgId,
        plan: plan?.code,
        members: membersToUpdate,
        otherAddons,
      },
      { headers }
    );

    // {addonName} addon successfully added to your plan.
    // Your plan has been successfully upgraded to Plus with 1 Client Portal & Generative AI for all your team members.
    // Your current Plus plan has been successfully upgraded with 1 Client Portal and Generative AI for all your team members.
    const successMsg: string[] = [];
    const addonsCnt = Object.keys(selectedAddons).length;

    if (settings.addonMode) {
      successMsg.push('add-on successfully added to your plan.');
    } else if (mode === 'addons') {
      successMsg.push('Thanks for your purchase! Your add-ons have been successfully added to your account.');
    } else if (selectedPlan !== currentPlanCode && selectedPlan !== null) {
      successMsg.push(`You've upgraded to ${plan?.name.en} Plan${!addonsCnt ? '.' : ''}`);
    } else {
      successMsg.push(`Your current ${plan?.name.en} Plan has been upgraded `);
    }

    if (addonsCnt && mode !== 'addons') {
      if (settings.addonMode) {
        const addon = Object.values(selectedAddons)[0];
        const title = store.addons.find((a) => a.code === addon.code)?.title || '';
        successMsg.unshift(title);
      } else {
        const addonsStr: string[] = [];
        successMsg.push('with ');
        Object.values(selectedAddons).forEach((addon) => {
          if (addon.code === BillingAddonCode.AI) {
            addonsStr.push(`Generative AI for all your team members`);
          } else {
            const title = store.addons.find((a) => a.code === addon.code)?.title || '';
            addonsStr.push(`${addon.quantity} ${title}`);
          }
        });
        successMsg.push(addonsStr.join(' & '));
        successMsg.push(` add-on${addonsCnt > 1 ? 's' : ''}. `);
      }
    }
    successMsg.push('Please refresh the page to apply changes.');

    settings.showSuccessToast && settings.showSuccessToast(successMsg.join(' '));
    settings.closePaywall && settings.closePaywall();
  } catch (_) {
    confirmError = true;
  }

  return { confirmError };
});

sample({
  source: { paywallStore: $paywallStore },
  clock: confirmSubscriptionEvent,
  fn: ({ paywallStore }) => {
    return paywallStore;
  },
  filter: ({ paywallStore }) => {
    return (
      !!paywallStore.settings.gatewayUrl &&
      (!!paywallStore.selectedPlan || !!Object.keys(paywallStore.selectedAddons).length)
    );
  },
  target: [startConfirmSubscriptionEvent, confirmSubscriptionFx],
});

$paywallStore.on(confirmSubscriptionFx.doneData, (state, fields) => {
  return {
    ...state,
    ...fields,
    fetchingOrderSummary: false,
    fetchingConfirmSubscription: false,
  };
});

/**
 * Show error dialog event
 */
export const showErrorDialogEvent = createEvent<boolean>();

$paywallStore.on(showErrorDialogEvent, (state, confirmError) => {
  return {
    ...state,
    confirmError,
  };
});

/**
 * Delete trial
 */

export const deleteTrialEvent = createEvent();

const deleteTrialFx = createEffect(async (store: PaywallState) => {
  const headers = {
    'everhelper-session-id': store.settings.sessionId,
  };
  try {
    await axiosClient.delete(`${store.settings.gatewayUrl}/ft:trial/businessOrgTrials/${store.settings.orgId}`, {
      headers,
    });
    window.location.reload();
  } catch (error) {
    console.error(error);
  }
});

sample({
  source: $paywallStore,
  clock: deleteTrialEvent,
  fn: (store) => store,
  target: deleteTrialFx,
});

/**
 * Start free trial
 */
export const startFreeTrialEvent = createEvent();

const startFreeTrialFx = createEffect(async (store: PaywallState) => {
  const headers = {
    'everhelper-session-id': store.settings.sessionId,
  };
  try {
    await axiosClient.post(
      `${store.settings.gatewayUrl}/ft:tasks/startFreeTrial`,
      {
        orgId: store.settings.orgId,
      },
      { headers }
    );
  } catch (error) {
    console.error(error);
  }
});

sample({
  source: $paywallStore,
  clock: startFreeTrialEvent,
  fn: (store) => store,
  target: startFreeTrialFx,
});

/**
 * Set paywall state
 */
export const setPaywallStateEvent = createEvent<Partial<PaywallState>>();

$paywallStore.on(setPaywallStateEvent, (state, payload) => {
  return {
    ...state,
    ...payload,
  };
});
